/*
  © 2025 Jhango
  https://www.jhango.com
*/

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');

    // Debugging: Check if the form is found
    if (!this.form) {
      console.error("❌ ProductForm Error: No form found inside <product-form>. Make sure your Liquid template includes <product-form>.");
      return;
    }

    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]');
    this.atcBtn = this.form.querySelector('.btn-atc');
    this.buyBtn = this.form.querySelector('.btn-buy');

    this.form.addEventListener('submit', event => this.onSubmit(event));

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event))
    );

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event));

    this.querySelectorAll('select[name="id"]').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select));
    });

    this.handleOutOfStockOptions();
  }

  onChangeProductOption(event) {
    try {
      const selectedOptions = [];

      // Collect all selected options
      this.productOptions.forEach(option => {
        const position = option.dataset.optionPosition;
        if (!position) return;

        if (option.type === 'radio' && option.checked) {
          selectedOptions[position - 1] = option.value;
        } else if (option.tagName === 'SELECT') {
          selectedOptions[position - 1] = option.value;
        }
      });

      // Find matching variant
      const variants = JSON.parse(this.form.dataset.variants || '[]');
      const matchingVariant = variants.find(variant => {
        return variant.options.every((optionValue, index) => {
          return selectedOptions[index] === optionValue;
        });
      });

      if (matchingVariant) {
        this.updateVariant(matchingVariant);
      }
    } catch (error) {
      console.error('Error handling product option change:', error);
    }
  }

  onChangeVariant(select) {
    try {
      const variantId = select.value;
      const variants = JSON.parse(this.form.dataset.variants || '[]');
      const selectedVariant = variants.find(variant => variant.id == variantId);

      if (selectedVariant) {
        this.updateVariant(selectedVariant);
      }
    } catch (error) {
      console.error('Error handling variant change:', error);
    }
  }

  updateVariant(variant) {
    // Update hidden variant ID input
    const variantInput = this.form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update price display
    this.updatePriceDisplay(variant);

    // Update stock status
    this.updateStockStatus(variant);

    // Update product options to reflect current variant
    this.updateProductOptions(variant);

    // Dispatch custom events for other components
    window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
      detail: variant
    }));

    // Fetch updated content for components that need it
    this.fetchUpdatedContent(variant);
  }

  updatePriceDisplay(variant) {
    // Find elements within the current product form context
    const productContainer = this.form.closest('.product-contents') || this.form.closest('.product-main') || document;
    const priceElement = productContainer.querySelector('.product-price-final');
    const comparePriceElement = productContainer.querySelector('.product-price-compare');
    const saleBadge = productContainer.querySelector('.product-sale-badge');
    const soldOutBadge = productContainer.querySelector('.product-sold-out-badge');

    // Also find add-to-cart button price elements
    const atcFinalPrice = productContainer.querySelector('#atc-final-price');
    const atcComparePrice = productContainer.querySelector('#atc-compare-price');

    if (priceElement) {
      const formattedPrice = this.formatMoney(variant.price);
      priceElement.innerHTML = formattedPrice;

      // Update price classes
      if (variant.compare_at_price > variant.price) {
        priceElement.classList.add('product-price-final-sale');
      } else {
        priceElement.classList.remove('product-price-final-sale');
      }
    }

    // Update add-to-cart button price (with hardcoded ₹ symbol)
    if (atcFinalPrice) {
      const formattedPrice = this.formatMoneyINR(variant.price);
      atcFinalPrice.innerHTML = formattedPrice;
    }

    if (comparePriceElement) {
      if (variant.compare_at_price > variant.price) {
        const formattedComparePrice = this.formatMoney(variant.compare_at_price);
        comparePriceElement.innerHTML = formattedComparePrice;
        comparePriceElement.closest('s').removeAttribute('hidden');
      } else {
        comparePriceElement.closest('s').setAttribute('hidden', '');
      }
    }

    // Update add-to-cart button compare price (with hardcoded ₹ symbol)
    if (atcComparePrice) {
      if (variant.compare_at_price > variant.price) {
        const formattedComparePrice = this.formatMoneyINR(variant.compare_at_price);
        atcComparePrice.innerHTML = formattedComparePrice;
        atcComparePrice.style.display = '';
      } else {
        atcComparePrice.style.display = 'none';
      }
    }

    if (saleBadge) {
      if (variant.compare_at_price > variant.price) {
        const discountType = saleBadge.dataset.discountType;
        let discountText = '';

        if (discountType === 'percentage') {
          const percentage = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discountText = `${percentage}%`;
        } else {
          const formattedSavings = this.formatMoney(variant.compare_at_price - variant.price);
          discountText = formattedSavings;
        }

        saleBadge.innerHTML = `Save ${discountText}`;
        saleBadge.removeAttribute('hidden');
      } else {
        saleBadge.setAttribute('hidden', '');
      }
    }

    if (soldOutBadge) {
      if (variant.available) {
        soldOutBadge.setAttribute('hidden', '');
      } else {
        soldOutBadge.removeAttribute('hidden');
      }
    }
  }

  updateStockStatus(variant) {
    const atcBtn = this.form.querySelector('.btn-atc');

    if (atcBtn) {
      if (variant.available) {
        atcBtn.disabled = false;
        atcBtn.textContent = atcBtn.dataset.addToCartText || 'Add to cart';
      } else {
        atcBtn.disabled = true;
        atcBtn.textContent = atcBtn.dataset.soldOutText || 'Sold out';
      }
    }
  }

  updateProductOptions(variant) {
    // Update option selections to match the variant
    variant.options.forEach((optionValue, index) => {
      const position = index + 1;

      // Update radio buttons
      const radioOption = this.form.querySelector(`input[type="radio"][data-option-position="${position}"][value="${optionValue}"]`);
      if (radioOption && !radioOption.checked) {
        radioOption.checked = true;
      }

      // Update select dropdowns
      const selectOption = this.form.querySelector(`select[data-option-position="${position}"]`);
      if (selectOption && selectOption.value !== optionValue) {
        selectOption.value = optionValue;
      }
    });
  }

  async fetchUpdatedContent(variant) {
    try {
      const response = await fetch(`${this.form.dataset.productUrl}?variant=${variant.id}`);
      if (response.ok) {
        const html = await response.text();
        const newDoc = new DOMParser().parseFromString(html, 'text/html');

        // Dispatch event with new document for components that need to update
        window.dispatchEvent(new CustomEvent('kt.product.variant.change.newDoc', {
          detail: newDoc
        }));
      }
    } catch (error) {
      console.warn('Failed to fetch updated variant content:', error);
    }
  }

  handleOutOfStockOptions() {
    try {
      const variants = JSON.parse(this.form.dataset.variants || '[]');

      // Mark unavailable options
      this.productOptions.forEach(option => {
        const position = option.dataset.optionPosition;
        const value = option.value;

        if (!position || !value) return;

        // Check if this option value has any available variants
        const hasAvailableVariant = variants.some(variant => {
          return variant.available && variant.options[position - 1] === value;
        });

        if (!hasAvailableVariant) {
          option.disabled = true;
          if (option.type === 'radio') {
            const label = option.closest('li') || option.nextElementSibling;
            if (label) {
              label.classList.add('disabled', 'text-muted');
            }
          }
        }
      });
    } catch (error) {
      console.warn('Error handling out of stock options:', error);
    }
  }

  onSubmit(event) {
    event.preventDefault();

    const atcBtn = this.form.querySelector('.btn-atc');
    if (atcBtn && !atcBtn.disabled) {
      this.addToCart();
    }
  }

  onClickBuyBtn(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    const params = new URLSearchParams(formData).toString();

    // Redirect to checkout with the current variant
    window.location.href = `/cart/add?${params}&return_to=/checkout`;
  }

  async addToCart() {
    const atcBtn = this.form.querySelector('.btn-atc');

    if (atcBtn) {
      const originalText = atcBtn.dataset.addToCartText || atcBtn.textContent.trim()

      // Set loading state
      atcBtn.style.width = `${atcBtn.offsetWidth}px`;
      atcBtn.classList.add('loading');
      atcBtn.disabled = true;
      atcBtn.setAttribute('aria-busy', 'true');

      try {
        const formData = new FormData(this.form);
        const cartContainer = document.querySelector('cart-container');

        if (cartContainer) {
          // Use cart-container for proper auto-open handling
          await cartContainer.add(formData);
        } else {
          // Fallback to direct API call
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Unable to add item to cart');
          }

          const result = await response.json();
          window.dispatchEvent(new CustomEvent('kt.cart.added', {
            detail: result
          }));

          // Update cart count
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          document.querySelectorAll('[data-cart-count]').forEach(elem => {
            elem.textContent = cart.item_count;
          });
        }

        // Always show ADDED state
        atcBtn.style.width = '';
        atcBtn.textContent = 'ADDED'
        atcBtn.classList.remove('loading')
        atcBtn.removeAttribute('aria-busy')

        // Revert after 2 seconds
        setTimeout(() => {
          atcBtn.textContent = originalText
          atcBtn.disabled = false
        }, 2000)
      } catch (error) {
        console.error('Add to cart error:', error);
        alert(error.message || 'Unable to add item to cart');
        
        // Remove loading state on error
        atcBtn.style.width = '';
        atcBtn.classList.remove('loading');
        atcBtn.disabled = false;
        atcBtn.removeAttribute('aria-busy');
      }
    }
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents).replace('.00', '');
    }

    // Fallback formatting if Shopify.formatMoney is not available
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`.replace('.00', '');
  }

  formatMoneyINR(cents) {
    // Format with ₹ symbol for add-to-cart button
    const amount = (cents / 100).toFixed(2);
    // Use Indian number formatting with commas
    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return `₹${formatted}`;
  }
}

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.listenVariantChange()
    this.initFullScreenGallery()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10,
      mousewheel: true
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      effect: this.dataset.effect,
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: this.dataset.paginationType,
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      },
      cubeEffect: {
        shadow: false
      },
      creativeEffect: {
        prev: {
          shadow: true,
          origin: 'left center',
          translate: ['-5%', 0, -200],
          rotate: [0, 100, 0]
        },
        next: {
          origin: 'right center',
          translate: ['5%', 0, -200],
          rotate: [0, -100, 0]
        }
      },
      scrollbar: {
        enabled: this.dataset.scrollbar === 'true',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
        hide: true
      },
      breakpoints: {
        0: { slidesPerView: 1 * (this.dataset.partialSlidesMobile === 'true' ? 1.2 : 1) },
        600: { slidesPerView: 1 }
      },
      on: {
        slideChange: this.onSlideChange.bind(this)
      }
    }
    return options
  }

  onSlideChange () {
    this.querySelectorAll('video').forEach(video => video.pause())

    setTimeout(() => {
      if (this.querySelector('.swiper-main .swiper-slide-active video')) {
        this.querySelector('.swiper-main-wrapper').setAttribute('data-active-slide-is-video', 'true')
      } else {
        this.querySelector('.swiper-main-wrapper').setAttribute('data-active-slide-is-video', 'false')
      }
    }, 500)
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')
      const pdpExtraContent = document.querySelector('#pdp-extra-content')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
            if (pdpExtraContent) {
              document.querySelector('#pdp-extra-content').style.top = '20px'
            }
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
          if (pdpExtraContent) {
            document.querySelector('#pdp-extra-content').style.top = `${headerGroup.clientHeight + 20}px`
          }
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }

  async initFullScreenGallery () {
    if (this.dataset.fullScreen === 'false') return

    const vendorScript = document.querySelector('script[src*="vendor-glightbox.min"]')

    vendorScript.onload = () => {
      window.GLightbox({
        selector: `[data-gallery="gallery-${this.dataset.productId}"]`,
        loop: true,
        openEffect: 'zoom',
        closeEffect: 'fade',
        slideEffect: this.dataset.fullScreenEffect
      })
    }
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

class QtySwitcher extends HTMLElement {
  constructor () {
    super()

    this.qtyInput = this.querySelector('input')
    this.minusBtn = this.querySelector('button.btn-qty-minus')
    this.plusBtn = this.querySelector('button.btn-qty-plus')

    this.qtyInput.addEventListener('change', () => this.onQtyInputChange())
    this.minusBtn.addEventListener('click', () => this.onMinusBtnClick())
    this.plusBtn.addEventListener('click', () => this.onPlusBtnClick())

    window.addEventListener('kt.product.variant.change.newDoc', (event) => this.onVariantChange(event))
  }

  onQtyInputChange () {
    this.validateButtons()
  }

  onMinusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) - 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  onPlusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) + 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  validateButtons () {
    if (this.qtyInput.getAttribute('min') && Number(this.qtyInput.value) <= Number(this.qtyInput.getAttribute('min'))) {
      this.minusBtn.disabled = true
      this.qtyInput.value = this.qtyInput.getAttribute('min')
    } else {
      this.minusBtn.disabled = false
    }
    if (this.qtyInput.getAttribute('max') && Number(this.qtyInput.value) >= Number(this.qtyInput.getAttribute('max'))) {
      this.plusBtn.disabled = true
      this.qtyInput.value = this.qtyInput.getAttribute('max')
    } else {
      this.plusBtn.disabled = false
    }
  }

  onVariantChange (event) {
    if (!this.closest('.product-block-atc')) return

    const newDoc = event.detail
    const newQtyInput = newDoc.querySelector('.product-block-atc qty-switcher input')

    this.qtyInput.setAttribute('max', newQtyInput.getAttribute('max'))
    this.validateButtons()
  }
}
customElements.define('qty-switcher', QtySwitcher)

class ProductStockIndicator extends HTMLElement {
  constructor () {
    super()

    this.progressBar = this.querySelector('.progress-bar')
    this.animateProgressBar()
    this.handleVariantChange()
  }

  animateProgressBar () {
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width
    }, 250)
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      this.querySelector('.product-block-stock-indicator-inner').style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail

      this.replaceWith(newDoc.querySelector('[id*="product-block-stock-indicator"]'))
      this.animateProgressBar()
    })
  }
}
customElements.define('product-stock-indicator', ProductStockIndicator)

class ProductQuantityBreaks extends HTMLElement {
  constructor () {
    super()

    this.handleInputChange()
    this.handleVariantChange()
  }

  handleInputChange (btn) {
    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        const qtyField = input.closest('form').querySelector('[name="quantity"]')
        qtyField.value = input.value
        qtyField.dispatchEvent(new CustomEvent('change'))

        const atcBtn = this.closest('form').querySelector('.btn-atc')

        setTimeout(() => {
          atcBtn.classList.add('animate__animated', 'animate__shakeX')
        }, 250)

        setTimeout(() => {
          atcBtn.classList.remove('animate__animated', 'animate__shakeX')
        }, 1500)
      })
    })
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      this.querySelector('.product-block-quantity-breaks-list').style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail

      this.replaceWith(newDoc.querySelector('product-quantity-breaks'))
      this.handleInputChange()
    })
  }
}
customElements.define('product-quantity-breaks', ProductQuantityBreaks)

class ProductQuickButton extends HTMLElement {
  constructor () {
    super()

    this.btn = this.querySelector('.btn-quick-view')
    this.modalCustomElem = document.querySelector('product-quick-view-modal')

    this.btn.addEventListener('click', async (event) => this.fetchProductData(event))
  }

  async fetchProductData (event) {
    event.preventDefault()

    this.setLoading(true)

    const response = await fetch(this.btn.dataset.productUrl)

    if (response.ok) {
      const text = await response.text()
      const newDoc = new DOMParser().parseFromString(text, 'text/html')

      document.querySelector('product-quick-view-modal').showModal(newDoc)
    } else {
      // TODO: Handle Errors
    }

    this.setLoading(false)
  }

  setLoading (isLoading) {
    if (isLoading) {
      this.btn.classList.add('loading')
      this.btn.disabled = true
      this.btn.setAttribute('aria-busy', 'true')
    } else {
      this.btn.classList.remove('loading')
      this.btn.disabled = false
      this.btn.removeAttribute('aria-busy')
    }
  }
}
customElements.define('product-quick-view-button', ProductQuickButton)

class ProductQuickViewModal extends HTMLElement {
  constructor () {
    super()

    this.modalElem = document.querySelector('#quick-view-modal')

    this.modalElem.addEventListener('hidden.bs.modal', () => {
      this.modalElem.querySelector('.product').innerHTML = '<div data-quick-view-contents></div>'
    })

    window.addEventListener('kt.cart.added', () => {
      // Only auto-close if cart drawer will open, and add delay to show ADDED state
      const autoOpen = window.theme_settings?.cart_drawer_auto_open !== false
      if (autoOpen) {
        setTimeout(() => {
          window.bootstrap.Modal.getOrCreateInstance(this.modalElem).hide()
        }, 1000)
      }
    })
  }

  async showModal (newDoc) {
    this.modalElem.querySelector('[data-quick-view-contents]').replaceWith(
      newDoc.querySelector('.product-main [data-quick-view-contents]'))

    this.modalElem.querySelector('.product-title').setAttribute('id', 'quick-view-modal-label')

    this.modalElem.querySelectorAll('.collapse-wrapper').forEach(collapseWrapper => {
      collapseWrapper.querySelector('[data-bs-toggle="collapse"]').setAttribute('aria-expanded', 'false')
      collapseWrapper.querySelector('.collapse').classList.remove('show')
    })

    this.modalElem.querySelectorAll('.product-options [name*="option"]').forEach(elem => {
      elem.setAttribute('name', `${elem.name}-qv`)
      elem.setAttribute('id', `${elem.id}-qv`)
    })

    this.modalElem.querySelectorAll('.product-options label').forEach(label => {
      label.setAttribute('for', `${label.getAttribute('for')}-qv`)
    })

    this.modalElem.querySelectorAll('.collapse').forEach(elem => {
      elem.addEventListener('shown.bs.collapse', () => {
        elem.closest('.product-block-collapse')?.scrollIntoView({ behavior: 'smooth' })
      })
    })

    // Re-initialize quick view add to cart handlers
    this.initQuickViewHandlers()

    window.bootstrap.Modal.getOrCreateInstance(this.modalElem).show()
    window.dispatchEvent(new CustomEvent('kt.product.quick_view.modal_shown'))
  }

  initQuickViewHandlers() {
    // Initialize quantity breaks in quick view
    this.initQuickViewQuantityBreaks()
    
    // Handle add to cart in quick view - support both btn-atc and custom-atc-btn
    const atcBtn = this.modalElem.querySelector('.btn-atc, .custom-atc-btn')
    if (atcBtn && !atcBtn.dataset.qvHandlerAttached) {
      atcBtn.dataset.qvHandlerAttached = 'true'
      
      const form = atcBtn.closest('form')
      if (!form) {
        console.error('Quick view form not found')
        return
      }

      const isCustomBtn = atcBtn.classList.contains('custom-atc-btn')
      
      atcBtn.addEventListener('click', async (e) => {
        e.preventDefault()
        e.stopPropagation()

        const originalHTML = isCustomBtn ? atcBtn.innerHTML : null
        const originalText = isCustomBtn ? null : atcBtn.textContent.trim()

        // Set loading state
        atcBtn.style.width = `${atcBtn.offsetWidth}px`;
        atcBtn.classList.add('loading')
        atcBtn.disabled = true
        atcBtn.setAttribute('aria-busy', 'true')
        
        if (isCustomBtn) {
          atcBtn.innerHTML = '<span class="custom-atc-text">LOADING...</span>'
        }

        try {
          const formData = new FormData(form)
          const cartContainer = document.querySelector('cart-container')

          if (cartContainer) {
            // Use cart-container for proper auto-open handling
            const variantId = formData.get('id')
            const quantity = parseInt(formData.get('quantity') || 1)
            const items = [{ id: Number(variantId), quantity }]
            await cartContainer.add({ items })
          } else {
            // Fallback to direct API call
            const variantId = formData.get('id')
            const quantity = parseInt(formData.get('quantity') || 1)
            const items = [{ id: Number(variantId), quantity }]
            
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items })
            })
            
            if (!response.ok) {
              const error = await response.json()
              throw new Error(error.message || 'Add to cart failed')
            }

            const result = await response.json()
            window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: result }))

            // Update cart count
            const cartResponse = await fetch('/cart.js')
            const cart = await cartResponse.json()
            document.querySelectorAll('[data-cart-count]').forEach(elem => {
              elem.textContent = cart.item_count
            })
          }

          // Always show ADDED state
          atcBtn.style.width = '';
          atcBtn.classList.remove('loading')
          atcBtn.removeAttribute('aria-busy')
          
          if (isCustomBtn) {
            atcBtn.innerHTML = '<span class="custom-atc-text">ADDED</span>'
          } else {
            atcBtn.textContent = 'ADDED'
          }

          // Revert after 2 seconds
          setTimeout(() => {
            if (isCustomBtn) {
              atcBtn.innerHTML = originalHTML
            } else {
              atcBtn.textContent = originalText
            }
            atcBtn.disabled = false
          }, 2000)
        } catch (error) {
          console.error('Quick view add to cart error:', error)
          atcBtn.style.width = '';
          atcBtn.classList.remove('loading')
          atcBtn.disabled = false
          atcBtn.removeAttribute('aria-busy')
          
          if (isCustomBtn) {
            atcBtn.innerHTML = originalHTML
          }
          
          alert('Unable to add item to cart: ' + error.message)
        }
      })
    }
  }

  initQuickViewQuantityBreaks() {
    // Initialize quantity breaks functionality in quick view modal
    const radios = this.modalElem.querySelectorAll('input[name^="qty-breaks-"]')
    const finalPriceEl = this.modalElem.querySelector('#atc-final-price')
    const comparePriceEl = this.modalElem.querySelector('#atc-compare-price')
    const qtyInput = this.modalElem.querySelector('#quantity-input')

    if (!radios || radios.length === 0) {
      // No quantity breaks in this product, skip
      return
    }

    radios.forEach(radio => {
      radio.addEventListener('change', function () {
        const qty = parseInt(this.dataset.quantity)
        const discount = parseFloat(this.dataset.discount)
        const basePrice = parseFloat(this.dataset.price.replace(/,/g, ''))

        const compareTotal = qty * basePrice
        const discountedTotal = compareTotal * ((100 - discount) / 100)

        // Format using INR
        const formatINR = amount => new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 2
        }).format(amount).replace("₹", "₹")

        if (finalPriceEl) {
          finalPriceEl.textContent = formatINR(discountedTotal)
        }
        
        if (comparePriceEl) {
          comparePriceEl.textContent = formatINR(compareTotal)
          comparePriceEl.style.display = ''
        }

        // Update quantity input
        if (qtyInput) {
          qtyInput.value = qty
        }
      })
    })
  }
}
customElements.define('product-quick-view-modal', ProductQuickViewModal)

class ProductDropupSelector extends HTMLElement {
  constructor () {
    super()

    this.productCard = this.closest('.product-card')
    this.dropdownBtn = this.querySelector('.dropdown-toggle')

    this.querySelector('.dropdown-menu').style.maxHeight =
      this.productCard.offsetHeight - this.dropdownBtn.offsetHeight - 2 + 'px'

    this.addEventListener('show.bs.dropdown', (event) => {
      const isMobile = window.matchMedia('(max-width: 499px').matches

      if (isMobile) {
        event.preventDefault()
        this.initOffcanvas()
      } else {
        this.productCard.querySelector('.product-card-img-wrapper').style.opacity = '.2'
      }
    })
    this.addEventListener('hide.bs.dropdown', (event) => {
      const isMobile = window.matchMedia('(max-width: 499px').matches

      if (isMobile) {
        event.preventDefault()
        this.removeOffcanvas()
      } else {
        this.productCard.querySelector('.product-card-img-wrapper').style.opacity = '1'
      }
    })

    this.querySelectorAll('.dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  async addToCart (btn) {
    const dropdown = window.bootstrap.Dropdown.getOrCreateInstance(this)
    if (dropdown) dropdown.hide()

    const originalText = this.dropdownBtn.dataset.originalText || this.dropdownBtn.textContent.trim()
    
    this.dropdownBtn.classList.add('loading')
    this.dropdownBtn.disabled = true
    this.dropdownBtn.setAttribute('aria-busy', 'true')

    const items = [{
      id: Number(btn.dataset.variantId),
      quantity: 1
    }]

    try {
      const cartContainer = document.querySelector('cart-container')
      
      if (cartContainer) {
        await cartContainer.add({ items })
      } else {
        // Fallback to direct API call
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        })
        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.message || 'Add to cart failed')
        }
      }

      // Show ADDED state
      this.dropdownBtn.textContent = 'ADDED'
      this.dropdownBtn.classList.remove('loading')

      // Update cart count
      await this.updateCartCount()

      // Revert back after 2 seconds
      setTimeout(() => {
        this.dropdownBtn.textContent = originalText
        this.dropdownBtn.disabled = false
        this.dropdownBtn.setAttribute('aria-busy', 'false')
      }, 2000)
    } catch (error) {
      console.error('Add to cart failed:', error)
      alert('Unable to add item to cart: ' + error.message)
      this.dropdownBtn.classList.remove('loading')
      this.dropdownBtn.disabled = false
      this.dropdownBtn.setAttribute('aria-busy', 'false')
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js')
      const cart = await response.json()
      document.querySelectorAll('[data-cart-count]').forEach(elem => {
        elem.textContent = cart.item_count
      })
    } catch (error) {
      console.error('Failed to update cart count:', error)
    }
  }

  initOffcanvas () {
    const html = `
      <div id="product-dropup-select-offcanvas" class="offcanvas offcanvas-bottom" tabindex="-1" aria-labelledby="product-dropup-select-offcanvas-title">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title" id="product-dropup-select-offcanvas-title">
            ${this.dropdownBtn.textContent}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" class="svg-icon-x" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="offcanvas-body p-0">
          <ul class="dropdown-menu">
            ${this.querySelector('.dropdown-menu').innerHTML}
          </ul>
        </div>
      </div>
    `
    document.body.insertAdjacentHTML('beforeend', html)

    window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').show()

    document.querySelectorAll('#product-dropup-select-offcanvas .dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => {
        this.addToCart(btn)
        window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').hide()
      })
    })

    document.querySelector('#product-dropup-select-offcanvas').addEventListener('hidden.bs.offcanvas', () => {
      document.querySelector('#product-dropup-select-offcanvas').remove()
    })
  }
}
customElements.define('product-dropup-selector', ProductDropupSelector)

class CrossSells extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.onCheckboxChange(checkbox))
    })

    this.querySelectorAll('select[name="variant-id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.querySelectorAll('.btn-atc').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex].dataset.variantImage

    if (variantImage.length) {
      select.closest('.product-card').querySelector('.product-card-img-wrapper img').setAttribute('src', variantImage)
    }

    this.updateTotalPrice()
  }

  onCheckboxChange (checkbox) {
    if (checkbox.checked) {
      checkbox.closest('.product-card').setAttribute('data-is-selected', 'true')
    } else {
      checkbox.closest('.product-card').setAttribute('data-is-selected', 'false')
    }
    this.updateTotalPrice()
  }

  updateTotalPrice () {
    let totalPrice = 0
    let totalComparePrice = 0

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      if (checkbox.checked) {
        const inputHidden = checkbox.closest('.product-card').querySelector('input[name="variant-id"][type="hidden"]')
        if (inputHidden) {
          totalPrice += Number(inputHidden.dataset.price)
          totalComparePrice += Number(inputHidden.dataset.compareAtPrice)
        }

        const select = checkbox.closest('.product-card').querySelector('select[name="variant-id"]')
        if (select) {
          totalPrice += Number(select[select.selectedIndex].dataset.price)
          totalComparePrice += Number(select[select.selectedIndex].dataset.compareAtPrice)
        }
      }
    })

    this.querySelectorAll('[data-total-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalPrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-savings]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice - totalPrice)}`.replace('.00', '')
    })

    if (totalPrice === 0) {
      this.querySelector('.btn-atc').disabled = true
    } else {
      this.querySelector('.btn-atc').disabled = false
    }

    if (totalComparePrice > totalPrice) {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s').removeAttribute('hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings').removeAttribute('hidden')
      })
    } else {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s').setAttribute('hidden', 'hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings').setAttribute('hidden', 'hidden')
      })
    }
  }

  async addToCart (atcBtn) {
    const originalText = atcBtn.textContent.trim()
    
    atcBtn.style.width = `${atcBtn.clientWidth}px`
    atcBtn.classList.add('loading')
    atcBtn.disabled = true
    atcBtn.setAttribute('aria-busy', 'true')

    const items = []

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      if (checkbox.checked) {
        const id = Number(checkbox.closest('.product-card').querySelector('[name="variant-id"]').value)

        items.push({
          id,
          quantity: 1
        })
      }
    })

    try {
      const cartContainer = document.querySelector('cart-container')
      
      if (!cartContainer) {
        throw new Error('Cart container not found')
      }

      await cartContainer.add({ items })

      // Show success state
      atcBtn.style.width = ''
      atcBtn.classList.remove('loading')
      atcBtn.removeAttribute('aria-busy')
      atcBtn.textContent = 'ADDED'

      // Revert after 2 seconds
      setTimeout(() => {
        atcBtn.textContent = originalText
        atcBtn.disabled = false
      }, 2000)
    } catch (error) {
      console.error('Cross-sells add to cart error:', error)
      
      // Reset button state on error
      atcBtn.style.width = ''
      atcBtn.classList.remove('loading')
      atcBtn.disabled = false
      atcBtn.removeAttribute('aria-busy')
      atcBtn.textContent = originalText
      
      alert('Unable to add items to cart: ' + error.message)
    }
  }
}
customElements.define('cross-sells', CrossSells)

class ProductCard extends HTMLElement {
  constructor () {
    super()

    this.handleCarouselHover()
  }

  handleCarouselHover () {
    if (window.matchMedia('(pointer: coarse)').matches) return

    const bsCarousel = window.bootstrap.Carousel.getOrCreateInstance(this.querySelector('.product-card-img-wrapper .carousel'))
    const carouselInner = this.querySelector('.product-card-img-wrapper .carousel-inner')
    let myInterval

    carouselInner.addEventListener('mouseenter', () => {
      setTimeout(() => {
        bsCarousel.next()
      }, 500)

      myInterval = setInterval(() => {
        bsCarousel.next()
      }, 2000)
    })

    carouselInner.addEventListener('mouseleave', () => {
      clearInterval(myInterval)
    })
  }
}
customElements.define('product-card', ProductCard)

class FileUploadPersonalizationField extends HTMLElement {
  constructor () {
    super()

    this.loadStyleScripts()

    const vendorScript = document.querySelector('script[src*="plugin-image-preview"]')
    vendorScript.onload = () => {
      this.init()
    }

    window.addEventListener('kt.product.quick_view.modal_shown', () => this.init())
  }

  loadStyleScripts () {
    if (document.querySelector('script[src*="plugin-image-preview"]')) return

    const style1 = document.createElement('link')
    style1.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.css')
    style1.setAttribute('rel', 'stylesheet')
    style1.setAttribute('integrity', 'sha256-a95jYCBL4++k1XyLYgulKmY33bIJIVYMsJO/RNytaJM=')
    style1.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(style1)

    const style2 = document.createElement('link')
    style2.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.css')
    style2.setAttribute('rel', 'stylesheet')
    style2.setAttribute('integrity', 'sha256-YsO8aMI20vSizdmc2mmWx2DU1wof4v60Nwy7hIBvdM8=')
    style2.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(style2)

    const script1 = document.createElement('script')
    script1.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.js')
    script1.setAttribute('integrity', 'sha256-6yXpr8+sATA4Q2ANTyZmpn4ZGP7grbIRNpe9s0Y+iO0=')
    script1.setAttribute('crossorigin', 'anonymous')
    script1.setAttribute('defer', 'defer')
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.js')
    script2.setAttribute('integrity', 'sha256-1vQHytMrpOsKFyUUPuEVhxeNH6UhMX3uNetDRvlSpwU=')
    script2.setAttribute('crossorigin', 'anonymous')
    script1.setAttribute('defer', 'defer')
    document.head.appendChild(script2)
  }

  async init () {
    await new Promise(resolve => setTimeout(resolve, 200))

    window.FilePond.registerPlugin(window.FilePondPluginImagePreview)
    const inputElem = this.querySelector('input[type="file"]')

    window.FilePond.create(inputElem, {
      storeAsFile: true,
      labelIdle: this.querySelector('[data-label-text]').innerHTML
    })
  }
}
customElements.define('file-upload-personalization-field', FileUploadPersonalizationField)

// Product block collapses
if (window.matchMedia('(max-width: 1199px').matches) {
  document.querySelectorAll('.product-block-collapse').forEach(elem => {
    elem.querySelector('.collapse').addEventListener('shown.bs.collapse', () => {
      elem.scrollIntoView({ behavior: 'smooth' })
    })
  })
}

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)

// Product Card Variant Selector Handler
document.addEventListener('DOMContentLoaded', () => {
  // Handle variant selector price updates
  document.querySelectorAll('.product-card-variant-select').forEach(select => {
    select.addEventListener('change', function() {
      const selectedOption = this.options[this.selectedIndex]
      const price = selectedOption.dataset.price
      const comparePrice = selectedOption.dataset.comparePrice
      const productId = this.dataset.productId
      
      // Update price display
      const priceElem = document.querySelector(`#variant-price-${productId}`)
      const comparePriceElem = document.querySelector(`#variant-compare-price-${productId}`)
      
      if (priceElem && price) {
        // Format price properly - money_without_currency shows 2 decimal places, so we keep them
        const formattedPrice = (parseFloat(price) / 100).toFixed(2)
        priceElem.textContent = `₹${formattedPrice}`
      }
      
      if (comparePriceElem && comparePrice && parseFloat(comparePrice) > parseFloat(price)) {
        // Format compare price with 2 decimal places to match initial rendering
        const formattedComparePrice = (parseFloat(comparePrice) / 100).toFixed(2)
        comparePriceElem.textContent = `₹${formattedComparePrice}`
        comparePriceElem.style.display = ''
      } else if (comparePriceElem) {
        comparePriceElem.style.display = 'none'
      }

      // Update button variant ID
      const btn = this.closest('form').querySelector('.product-card-variant-atc')
      if (btn) {
        btn.dataset.variantId = this.value
      }
    })
  })

  // Handle variant selector add to cart
  document.querySelectorAll('.product-card-variant-atc').forEach(btn => {
    btn.addEventListener('click', async function(e) {
      e.preventDefault()
      e.stopPropagation()

      const form = this.closest('form')
      const variantId = this.dataset.variantId || form.querySelector('select[name="id"]').value
      const originalText = this.dataset.originalText || this.querySelector('.btn-text')?.textContent || 'ADD TO CART'
      const btnText = this.querySelector('.btn-text')

      // Set loading state
      this.classList.add('loading')
      this.disabled = true

      try {
        const items = [{ id: Number(variantId), quantity: 1 }]
        const cartContainer = document.querySelector('cart-container')
        
        if (cartContainer) {
          await cartContainer.add({ items })
        } else {
          // Fallback to direct API call
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          })
          if (!response.ok) throw new Error('Add to cart failed')
        }

        // Show ADDED state
        if (btnText) {
          btnText.textContent = 'ADDED'
        } else {
          this.textContent = 'ADDED'
        }
        this.classList.remove('loading')

        // Update cart count
        const cartResponse = await fetch('/cart.js')
        const cart = await cartResponse.json()
        document.querySelectorAll('[data-cart-count]').forEach(elem => {
          elem.textContent = cart.item_count
        })

        // Revert after 2 seconds
        setTimeout(() => {
          if (btnText) {
            btnText.textContent = originalText
          } else {
            this.textContent = originalText
          }
          this.disabled = false
        }, 2000)
      } catch (error) {
        console.error('Add to cart error:', error)
        this.classList.remove('loading')
        this.disabled = false
        alert('Unable to add item to cart')
      }
    })
  })

  // Handle single variant add to cart
  document.querySelectorAll('.product-card-atc-single').forEach(btn => {
    btn.addEventListener('click', async function(e) {
      e.preventDefault()
      e.stopPropagation()

      const form = this.closest('form')
      const variantId = this.dataset.variantId || form.querySelector('input[name="id"]').value
      const originalText = this.dataset.originalText || this.querySelector('.btn-text')?.textContent || 'ADD TO CART'
      const btnText = this.querySelector('.btn-text')

      // Set loading state
      this.classList.add('loading')
      this.disabled = true

      try {
        const items = [{ id: Number(variantId), quantity: 1 }]
        const cartContainer = document.querySelector('cart-container')
        
        if (cartContainer) {
          await cartContainer.add({ items })
        } else {
          // Fallback to direct API call
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          })
          if (!response.ok) throw new Error('Add to cart failed')
        }

        // Show ADDED state
        if (btnText) {
          btnText.textContent = 'ADDED'
        } else {
          this.textContent = 'ADDED'
        }
        this.classList.remove('loading')

        // Update cart count
        const cartResponse = await fetch('/cart.js')
        const cart = await cartResponse.json()
        document.querySelectorAll('[data-cart-count]').forEach(elem => {
          elem.textContent = cart.item_count
        })

        // Revert after 2 seconds
        setTimeout(() => {
          if (btnText) {
            btnText.textContent = originalText
          } else {
            this.textContent = originalText
          }
          this.disabled = false
        }, 2000)
      } catch (error) {
        console.error('Add to cart error:', error)
        this.classList.remove('loading')
        this.disabled = false
        alert('Unable to add item to cart')
      }
    })
  })
})

customElements.define('product-form', ProductForm);

